import {FaKey} from "react-icons/fa";
import {FaAirbnb} from "react-icons/fa6";
import {Ban, Radio} from "lucide-react";
import "./JobSourceBadge.css";

const SOURCE_STYLES = {
    OWNER: {color: "#222", backgroundColor: "#fff8e1"},
    AIRBNB: {color: "#ff385c", backgroundColor: "#fff"},
    BOOKING_COM: {color: "#003b95", backgroundColor: "#fff"},
    MANUAL: {color: "#4b5563", backgroundColor: "#f3f4f6"},
    URBANAIDE: {color: "#ed6c00", backgroundColor: "#fff7ed"},
    OTHER: {color: "#256f67", backgroundColor: "#eef8f6"},
};

const URBANAIDE_FALLBACK_SOURCE = {code: "URBANAIDE", label: "UrbanAide"};
const URBANAIDE_ICON_PATH = `${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/favicon.png`;
const GUESTY_RESERVATION_BASE_URL = "https://app.guesty.com/reservations";

function buildGuestyReservationUrl(reservationId) {
    return `${GUESTY_RESERVATION_BASE_URL}/${encodeURIComponent(reservationId)}/summary`;
}

function stopClickPropagation(event) {
    event.stopPropagation();
}

function isTaskJob(job) {
    return typeof job?.job_type === "string" && job.job_type.toLowerCase() === "task";
}

function getDisplaySource(job) {
    if (isTaskJob(job)) {
        return null;
    }
    const source = job?.booking_source;
    if (source?.code && source?.label) {
        return source;
    }
    return URBANAIDE_FALLBACK_SOURCE;
}

function SourceIcon({code, compact = false}) {
    if (code === "OWNER") {
        return <FaKey aria-hidden="true" />;
    }
    if (code === "AIRBNB") {
        return <FaAirbnb aria-hidden="true" />;
    }
    if (code === "BOOKING_COM") {
        return <span className="job-source-booking-glyph" aria-hidden="true">B.</span>;
    }
    if (code === "MANUAL") {
        return <Ban aria-hidden="true" size={compact ? 12 : 16} strokeWidth={2.2} />;
    }
    if (code === "URBANAIDE") {
        return <img className="job-source-urban-aide-icon" src={URBANAIDE_ICON_PATH} alt="" aria-hidden="true" />;
    }
    return <Radio aria-hidden="true" size={compact ? 12 : 16} strokeWidth={2.2} />;
}

export default function JobSourceBadge({job, showLabel = false, compact = false}) {
    const source = getDisplaySource(job);
    if (!source) {
        return null;
    }

    const style = SOURCE_STYLES[source.code] || SOURCE_STYLES.OTHER;
    const isUrbanAideFallback = source.code === "URBANAIDE";
    const tooltip = isUrbanAideFallback
        ? "Source: UrbanAide\nSystem: COS\nNot from Guesty"
        : [
            `Source: ${source.label}`,
            `Code: ${source.code}`,
            source.external_reservation_id ? `Reservation: ${source.external_reservation_id}` : null,
        ].filter(Boolean).join("\n");
    const icon = (
        <span className="job-source-icon" style={style}>
            <SourceIcon code={source.code} compact={compact} />
        </span>
    );
    const linkedIcon = source.external_reservation_id ? (
        <a
            href={buildGuestyReservationUrl(source.external_reservation_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="job-source-link"
            aria-label={`Open ${source.label} reservation in Guesty`}
            onClick={stopClickPropagation}
        >
            {icon}
        </a>
    ) : icon;

    return (
        <span
            title={tooltip}
            aria-label={isUrbanAideFallback
                ? "Booking source: UrbanAide; System: COS; Not from Guesty"
                : `Booking source: ${source.label}`}
            className={`job-source-badge${compact ? " job-source-badge-compact" : ""}`}
            style={{color: style.color}}
        >
            {linkedIcon}
            {showLabel ? <span className="job-source-label">{source.label}</span> : null}
        </span>
    );
}
